import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import WorkCard from "@/components/feature/WorkCard";
import { NewsRow } from "@/components/feature/NewsCard";
import { works } from "@/mocks/works";
import { newsList } from "@/mocks/news";
import { usePageMeta } from "@/hooks/usePageMeta";

const suggestions = ["家庭叙事", "海边", "悬疑", "新作", "影评", "幕后"];

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get("q") ?? "";
  const [keyword, setKeyword] = useState(query);

  usePageMeta(
    query ? `「${query}」搜索结果 - 大桥未久影视网站` : "搜索 - 大桥未久影视作品与资讯",
    query
      ? `在本站搜索「${query}」相关的影视作品与资讯内容，查看作品详情与相关动态。`
      : "搜索大桥未久的影视作品与相关资讯，支持按标题、类型、标签与关键词检索。",
    "搜索,大桥未久,影视作品,资讯检索",
  );

  useEffect(() => {
    setKeyword(query);
  }, [query]);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const value = keyword.trim();
    if (!value) {
      setSearchParams({});
      return;
    }
    setSearchParams({ q: value });
  };

  const trimmed = query.trim().toLowerCase();

  const workResults = useMemo(() => {
    if (!trimmed) return [];
    return works.filter((work) => {
      const haystack = [
        work.title,
        work.genre,
        work.director,
        work.year,
        work.tags.join(" "),
        work.cast.map((member) => member.name).join(" "),
        work.synopsis.join(" "),
      ]
        .join(" ")
        .toLowerCase();
      return haystack.includes(trimmed);
    });
  }, [trimmed]);

  const newsResults = useMemo(() => {
    if (!trimmed) return [];
    return newsList.filter((item) => {
      const haystack = `${item.title} ${item.summary} ${item.category} ${item.content.join(" ")}`.toLowerCase();
      return haystack.includes(trimmed);
    });
  }, [trimmed]);

  const total = workResults.length + newsResults.length;
  const quickWorks = works.filter((item) => item.status === "new").slice(0, 4);

  return (
    <SiteLayout>
      <section className="w-full border-b border-background-200 bg-background-100/60">
        <div className="mx-auto w-full max-w-[1280px] px-4 pb-8 pt-6 md:px-6 md:pb-10 md:pt-8">
          <Breadcrumbs items={[{ label: "首页", to: "/" }, { label: "搜索" }]} />

          <div className="mt-6 max-w-3xl">
            <p className="font-label text-[11px] font-semibold uppercase tracking-[0.18em] text-accent-700">
              Search
            </p>
            <h1 className="mt-3 text-2xl font-bold tracking-tight text-foreground-950 md:text-[32px]">
              {query ? `「${query}」的搜索结果` : "搜索作品与资讯"}
            </h1>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600">
              可搜索作品标题、类型标签、导演与演员姓名，也可以搜索资讯标题与正文关键词。
            </p>
          </div>

          <form
            onSubmit={handleSubmit}
            role="search"
            className="mt-6 flex w-full max-w-2xl flex-col gap-2 rounded-lg border border-background-300 bg-background-50 p-2 sm:flex-row sm:items-center"
          >
            <div className="flex min-w-0 flex-1 items-center gap-2 px-2">
              <i className="ri-search-line text-base text-foreground-400"></i>
              <input
                type="search"
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="输入作品标题、演员姓名或资讯关键词"
                aria-label="搜索关键词"
                className="min-h-[44px] w-full bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none"
              />
            </div>
            <button
              type="submit"
              className="inline-flex min-h-[44px] cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              搜索
            </button>
          </form>
        </div>
      </section>

      <div className="mx-auto w-full max-w-[1280px] px-4 py-8 md:px-6 md:py-10">
        {!trimmed ? (
          <div>
            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <h2 className="text-sm font-semibold text-foreground-950">热门搜索</h2>
              <div className="mt-3 flex flex-wrap gap-2">
                {suggestions.map((item) => (
                  <button
                    key={item}
                    type="button"
                    onClick={() => setSearchParams({ q: item })}
                    className="min-h-[38px] cursor-pointer whitespace-nowrap rounded-full border border-background-300 px-3.5 text-xs text-foreground-600 transition-colors hover:border-accent-400 hover:text-accent-700"
                  >
                    {item}
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-8">
              <h2 className="text-lg font-bold text-foreground-950">近期更新作品</h2>
              <p className="mt-1 text-xs text-foreground-500">
                也可以从下面这些作品开始浏览
              </p>
              <div className="mt-5 grid grid-cols-2 gap-4 sm:grid-cols-3 md:gap-6 lg:grid-cols-4">
                {quickWorks.map((work, index) => (
                  <WorkCard key={work.id} work={work} delay={index * 50} />
                ))}
              </div>
            </div>
          </div>
        ) : (
          <div className="grid gap-10 lg:grid-cols-[minmax(0,1fr)_300px]">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-foreground-500">
                <p>
                  找到{" "}
                  <span className="font-label font-semibold text-foreground-900">{total}</span>{" "}
                  条相关内容
                </p>
                <Link
                  to="/category/movie"
                  className="inline-flex items-center gap-1 whitespace-nowrap text-xs text-foreground-500 transition-colors hover:text-accent-700"
                >
                  浏览完整作品档案
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>

              {total === 0 ? (
                <div className="mt-4 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-50 px-6 py-16 text-center">
                  <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-foreground-400">
                    <i className="ri-search-eye-line text-2xl"></i>
                  </span>
                  <h2 className="mt-4 text-base font-semibold text-foreground-950">
                    没有找到相关内容
                  </h2>
                  <p className="mt-2 max-w-md text-sm leading-relaxed text-foreground-500">
                    换个关键词试试，例如作品名称、年份、类型，或从作品档案中直接浏览。
                  </p>
                  <div className="mt-5 flex flex-wrap justify-center gap-2">
                    {suggestions.slice(0, 3).map((item) => (
                      <button
                        key={item}
                        type="button"
                        onClick={() => setSearchParams({ q: item })}
                        className="min-h-[40px] cursor-pointer whitespace-nowrap rounded-full border border-background-300 px-4 text-xs text-foreground-600 transition-colors hover:border-accent-400 hover:text-accent-700"
                      >
                        {item}
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                <>
                  {workResults.length > 0 && (
                    <section className="mt-5">
                      <h2 className="flex items-center gap-2 text-base font-bold text-foreground-950">
                        <span className="h-3.5 w-1 rounded-full bg-accent-500"></span>
                        作品结果
                        <span className="font-label text-xs font-normal text-foreground-400">
                          {workResults.length}
                        </span>
                      </h2>
                      <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-3 md:gap-6">
                        {workResults.map((work, index) => (
                          <WorkCard key={work.id} work={work} delay={index * 40} />
                        ))}
                      </div>
                    </section>
                  )}

                  {newsResults.length > 0 && (
                    <section className="mt-10">
                      <h2 className="flex items-center gap-2 text-base font-bold text-foreground-950">
                        <span className="h-3.5 w-1 rounded-full bg-accent-500"></span>
                        资讯结果
                        <span className="font-label text-xs font-normal text-foreground-400">
                          {newsResults.length}
                        </span>
                      </h2>
                      <div className="mt-3 rounded-lg border border-background-200 bg-background-50 px-5">
                        {newsResults.map((item) => (
                          <NewsRow key={item.id} item={item} />
                        ))}
                      </div>
                    </section>
                  )}
                </>
              )}
            </div>

            <aside className="min-w-0">
              <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                <h2 className="text-sm font-semibold text-foreground-950">搜索提示</h2>
                <ul className="mt-3 space-y-2.5 text-xs leading-relaxed text-foreground-600">
                  <li className="flex gap-2">
                    <i className="ri-checkbox-circle-line mt-0.5 text-accent-600"></i>
                    支持中文标题与年份，如「2023」「海边书店」
                  </li>
                  <li className="flex gap-2">
                    <i className="ri-checkbox-circle-line mt-0.5 text-accent-600"></i>
                    可搜索类型标签，如「悬疑」「治愈」
                  </li>
                  <li className="flex gap-2">
                    <i className="ri-checkbox-circle-line mt-0.5 text-accent-600"></i>
                    搜索结果由浏览器本地即时生成
                  </li>
                </ul>
              </div>

              {newsResults.length === 0 && workResults.length > 0 && (
                <div className="mt-6 rounded-lg bg-primary-50 p-5">
                  <h2 className="text-sm font-semibold text-foreground-950">看看近期资讯</h2>
                  <div className="mt-3 space-y-3">
                    {newsList.slice(0, 3).map((item) => (
                      <Link
                        key={item.id}
                        to={`/news/${item.id}`}
                        className="group block cursor-pointer"
                      >
                        <p className="line-clamp-2 text-xs leading-relaxed text-foreground-800 transition-colors group-hover:text-accent-700">
                          {item.title}
                        </p>
                        <p className="mt-1 text-[11px] text-foreground-400">{item.date}</p>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </aside>
          </div>
        )}
      </div>
    </SiteLayout>
  );
}